import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Typography, withStyles, FormControl, FormGroup, FormControlLabel, Checkbox } from '@material-ui/core';
import LegendUnit from './LegendUnit';
import {ExpandMore, ExpandLess} from '@material-ui/icons';
import {getLabels} from '../constants/labels';

const styles = theme => ({
    container: {
        paddingBottom: 10,
        width:'100%'
    },
    unit: {
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis"
    },
    children: {
        display: 'flex',
        flexDirection:'column',
        paddingLeft:'5px',
        width:"100%",
        color:"#384257"
    },
    father: {
        display: 'flex',
        backgroundColor:"#e4e7ea",
        width:"100%",
        marginLeft:'-30px',
        paddingRight:'60px',
        flexDirection:'row',
        alignItems:'center'
    },
    label: {
        fontSize:'13.5px',
        letterSpacing:'0px',
        color:"#384257",
    },
    expandIcon: {
        cursor: 'pointer',
        color:'#384257',
        marginRight:'-50px',
        width:"30px"
    },
    title: {
        fontWeight:'bold',
        fontSize:'15px',
        color:"#384257"
    },
});

const SHOW_ALL = false;

function Legend({ classes, items, handleChange = () => {}, checked, checkedAll, displayables={}, handleDisplay, language, handleCollapse, collapsed }) {
    const itemId = (index) => `element_${index}`;
    const labels = getLabels(language);
    // Discard 1st legend level with no children
    const filteredItems = items.filter(p=>{
        const level = p?.id.split('.').length;
        if (level === 1 && !items.find(op=>op.id.startsWith(`${p.id}.`)) && p.quantity===undefined) {
            return false;
        }
        return true;
    })

    let groupHeader = [];
    let groupHeaderInfo = null;  // this will be used to save the groupHeader props
    let lastHeaderId = null;
    let lastHeaderItem = null;
    let lastHeaderQuantity = 0;  // this will be used to add up the sum of the lvl 2 items
    let lastFatherItem = null;
    let lastFatherQuantity = 0;  // this will be used to add up the sum of the lvl 1 items
    let subgroup = [];

    // The following function renders a single LegendUnit object.
    const getLegendUnit = (item, index, level, expandIcon, isTitle ) => {
        return (
        <Grid item container xs={12} style={{paddingTop: level === 1 ? 5 : 0}}
            key={itemId(index)} data-testid={itemId(index)} id={itemId(index)}>
            <Grid item xs>
                <LegendUnit name={item.name} index={index}
                    color={item.color}
                    number={item.quantity? item.quantity : 0}
                    checked={checked?.[item.id]}
                    onClick={() => handleChange(item.id, checked && !checked[item.id])}
                    handleDisplay={() => handleDisplay(item.id)}
                    ExpandIcon={expandIcon === true? (!SHOW_ALL && (displayables[item.id] ? ExpandLess : ExpandMore)): null}
                    isTitle={isTitle}
                    level={level}
                />
            </Grid>
        </Grid>
    )}

    // The following function forms a group of LegendUnits, rendering the groupHeader depending on if it has children or not.
    const getNewGroup = (id, groupHeaderInput, lastHeaderIdInput, subgroupInput, groupHeaderInfo ) => {
        const newGroupHeader = groupHeaderInfo!==null? getLegendUnit(groupHeaderInfo['item'], groupHeaderInfo['index'], 2, true, false ) : null
        return (
            <div key={`group_${id}`} style={{ width: "100%" }}>
                {subgroup.length === 0? groupHeaderInput : newGroupHeader}
                {((lastHeaderIdInput && displayables[lastHeaderIdInput]) || SHOW_ALL) && subgroupInput}
            </div>
        );
    };

    return <Grid container className={classes.container}>
        <FormControl component="fieldset" style={{width:'100%'}}>
            <FormGroup>
                <Grid className={classes.father}>
                <FormControlLabel
                    style={{paddingLeft:20, marginRight:'auto'}}
                    control={
                        <Checkbox
                            checked={checkedAll}
                            onChange={() => handleChange('__all__', !checkedAll)}
                            style={{color:"#384257"}}
                        />
                    }
                    label={<Typography className={classes.title}>{labels.program}</Typography>}
                />
                    {( collapsed === false &&
                        <ExpandLess className={classes.expandIcon} onClick={handleCollapse}/>
                    )}
                    {( collapsed === true&&
                        <ExpandMore className={classes.expandIcon} onClick={handleCollapse}/>
                    )}
                </Grid>
                <Grid className={classes.children}>
                { collapsed === false && filteredItems.map((item, index) => {  // This part is rendered only if the 'Programs' label object is not collapsed.
                    const itemIdArray = item.id.split('.');  // the length of this array represents the hierarchy of the object.
                    if (itemIdArray.length <= 2) {
                        const group = getNewGroup(index, groupHeader, lastHeaderId, subgroup, groupHeaderInfo );
                        subgroup = [];
                        groupHeader = [];
                        if (itemIdArray.length === 2) {  // A lvl 2 object may or may not be a father item. Its parent is a level 1 object.
                            if(lastHeaderItem && lastHeaderItem.quantity === 0) lastHeaderItem.quantity = lastHeaderQuantity;  // We finish counting the coincidences for the previous lvl 2 item
                            if(lastFatherItem) lastFatherQuantity += item.quantity  // If the item does not have children of its own, then it has a quantity. We add that quantity to the lvl 1 parent.
                            lastHeaderQuantity = 0; // The quantity is reset, and we declare the new lastHeaderItem
                            lastHeaderItem = item;
                            lastHeaderId = lastHeaderItem.id;
                            groupHeader = [
                                getLegendUnit(item, index, 2, false, false )
                            ]
                            groupHeaderInfo = {'item': item, 'index': index}
                            if(lastFatherItem && displayables[lastFatherItem.id]) return group;
                        }
                        else {  // lvl 1 item
                            let displayedItems = null;
                            if(lastFatherItem && lastFatherItem.quantity === 0) lastFatherItem.quantity = lastFatherQuantity;
                            if(lastFatherItem && displayables[lastFatherItem.id]){  // It renders the children group only if the lvl 1 father is not collapsed.
                                displayedItems = [
                                    group,
                                    getLegendUnit(item, index, itemIdArray.length, true, true )
                                ]
                            }
                            else {
                                displayedItems = [
                                    getLegendUnit(item, index, itemIdArray.length, true, true )
                                ]
                            }
                            lastFatherQuantity = 0; // The quantity is reset, and we declare the new lastFatherItem
                            lastFatherItem = item;
                            return displayedItems
                        }
                    }
                    else if (itemIdArray.length > 2) {   // Lowest ranking item
                        if(lastFatherItem) lastFatherQuantity += item.quantity;
                        if(lastHeaderItem) lastHeaderQuantity += item.quantity;
                        subgroup.push(getLegendUnit(item, index, itemIdArray.length, false, false ));
                    }
                    return '';
                })}
                {lastFatherItem && lastFatherItem.quantity === 0? lastFatherItem.quantity = lastFatherQuantity: ''}
                {groupHeader.length > 0 && lastFatherItem && displayables[lastFatherItem.id] && getNewGroup('last', groupHeader, lastHeaderItem.id, subgroup, groupHeaderInfo ) }
                </Grid>
            </FormGroup>
        </FormControl>
    </Grid>
}

Legend.propTypes = {
    items: PropTypes.array.isRequired,
}

export default withStyles(styles)(Legend);
